import { Component, OnInit, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { GraphQLError } from "graphql";
import { CurrentUserService } from "../services/current-user.service";
import { CurrentUser } from "../interfaces/current-user.interface";
import { Router, RouterModule } from "@angular/router";
import { TooltipModule } from "ngx-bootstrap/tooltip";
import { SharedModule } from "../shared/shared.module";
import { environment } from "../../environments/environment";
import { AuthTokenService } from '../services/auth-token.service';
import { MegaMenuModule, MegaMenu } from 'primeng/megamenu';
import { MegaMenuItem, MenuItem } from "primeng/api";
import { CartService } from '../services/cart.service';
import { FeatureAccessService, PermissionSubjectClass, PermittedAction } from '../services/feature-access.service';
import { DialogService } from 'primeng/dynamicdialog';
import { ImpersonatableUserSearchComponent } from '../impersonatable-user-search/impersonatable-user-search.component';
import { ButtonModule } from "primeng/button";
import { ImpersonationService } from '../services/impersonation.service';
import { appConstants } from '../app-constants';
import { BrandingService } from '../services/branding.service';
import { combineLatest, } from 'rxjs';
import { AuthFeature, BrandingByHostQuery, BrandProtectionFeature, InfoLink, TranslatedInfoLinks } from '../graphql/gql';
import { LanguageService, SupportedLanguage } from '../services/language.service';
import { CardModule } from 'primeng/card';
import { NgTemplateOutlet } from '@angular/common'
import { resolve as puResolve } from '@primeuix/utils';

interface ICustomMegaMenuItem<TMenuItem extends MenuItem> extends MegaMenuItem {
  items?: TMenuItem[][];
}

/**
 *  Extended interface for custom menu items with impersonation support
 */
interface CustomMenuItem extends MenuItem {
  /**
   * Injection site for user impersonation.
   */
  isImpersonationItem?: boolean;

  /**
   * Injection site for user details like client number.
   */
  isUserDataItem?: boolean;

  items?: CustomMenuItem[] | undefined;
}

interface CustomMegaMenuItem extends ICustomMegaMenuItem<CustomMenuItem> {};

/**
 * HeaderComponent class represents the header component of the application.
 */
@Component({
  selector: 'app-header',
  imports: [
    RouterModule,
    MegaMenuModule,
    TooltipModule,
    SharedModule,
    ButtonModule,
    CardModule,
    ButtonModule,
    NgTemplateOutlet
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  providers: [DialogService],
})

export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  currentUser: CurrentUser | null | undefined = null;
  errors: readonly GraphQLError[] | undefined;
  navigationItems: CustomMegaMenuItem[] = [];

  @ViewChild('megaMenuRef') megaMenu!: MegaMenu;

  private windowBlurHandler?: () => void;
  private messageHandler?: (event: MessageEvent) => void;
  private globalClickHandler?: (event: Event) => void;
  public legacyFrontendURL = environment.legacyFrontendURL;
  private brandMonitoringPortalURL?: string = environment.brandMonitoringPortalURL;

  constructor(
    private currentUserService: CurrentUserService,
    private brandingService: BrandingService,
    private router: Router,
    private authTokenService: AuthTokenService,
    public cartService: CartService,
    private featureAccessService: FeatureAccessService,
    private dialogService: DialogService,
    private impersonationService: ImpersonationService,
    private languageService: LanguageService
  ) { }

  private infoLinksFromLanguage(branding: BrandingByHostQuery, language: SupportedLanguage): TranslatedInfoLinks {
    const existing = branding.brandingByHost.infoLinks.find(info => info.language == language);

    if (existing !== undefined) {
      return existing;
    } else {
      const defaultLinks = branding.brandingByHost.infoLinks.find(info => info.language == this.languageService.defaultLanguage)

      if (defaultLinks !== undefined) {
        return defaultLinks;
      } else {
        return {
          language: this.languageService.defaultLanguage,
          links: []
        };
      }
    }
  }

  async ngOnInit() {
    this.currentUserService.currentUser.subscribe(async result => {
      this.currentUser = result?.currentUser as CurrentUser;
    });

    combineLatest(
      [
        this.featureAccessService.visibilityChange$,
        this.languageService.currentLanguage$,
        this.brandingService.brandingByHost$
      ]
    ).subscribe(([_, language, branding]) => {
      this.navigationItems = this.generateNavigationItems(this.infoLinksFromLanguage(branding, language));
    });
  }

  private infoLinkToMenuItem(infoLink: InfoLink): MenuItem {
    // those types are compatible at time of writing...
    return infoLink;
  }

  /**
   * Check if a menu item contains routing information. Items that do not address other resources are considered
   * for display only.
   */
  protected isRoutingItem(menuItem: MenuItem): boolean {
    return (puResolve(menuItem.routerLink) ?? puResolve(menuItem.url) ?? menuItem.command) !== undefined
  }

  /**
   * Cosmetic items are just for show, but not factored for submenu visibility.
   */
  private isCosmeticItem(menuItem: CustomMenuItem): boolean {
    return !!menuItem.separator;
  }

  private isVisibleItem(menuItem: MenuItem): boolean {
    return menuItem.visible === undefined || puResolve(menuItem.visible);
  }

  /**
   * Functional items are stuff you could click on.
   *
   * This implementation explicitly does *not* descend into subitems since the removal is running bottom-up. We
   * only check the simple presence of a submenu here.
   */
  private isFunctionalItem(menuItem: CustomMenuItem): boolean {
    return this.isVisibleItem(menuItem) &&
      (
        puResolve(menuItem.isImpersonationItem) ||
        puResolve(menuItem.isUserDataItem) ||
        this.isRoutingItem(menuItem) ||
        !!menuItem.items
      );
  }

  /**
   * @returns Tuple of kept items and a flag indicating if any of them are functional.
   */
  private deleteEmptyMenus<TItem extends CustomMenuItem>(items: TItem[] | undefined): [items: TItem[] | undefined, anyFunctional: boolean] {
    if (items === undefined) {
      return [items, false];
    }

    const keepers = items.filter((item) => {
      const [subItems, anyFunctional] = this.deleteEmptyMenus(item.items);

      if (anyFunctional) {
        item.items = subItems;
      } else {
        item.items = undefined;
      }

      return this.isFunctionalItem(item) || this.isCosmeticItem(item);
    });

    return [keepers, keepers.some(item => this.isFunctionalItem(item))];
  }

  /**
   * Remove non-functional menu entries that do not provide any routing and that do not have submenus.
   */
  private deleteEmptyMegaMenus(items: CustomMegaMenuItem[]): CustomMegaMenuItem[] {
    return items.reduce(
      (keptTopLevelItems, topLevelItem) => {
        if (topLevelItem.items !== undefined) {
          topLevelItem.items.forEach((column) => this.deleteEmptyMenus(column)[0])
          topLevelItem.items = topLevelItem.items.reduce(
            (newColumn: CustomMenuItem[][], column) => {
              const keepers = column.reduce(
                (keptParagraphs, paragraph) => {
                  if (this.isFunctionalItem(paragraph)) {
                    keptParagraphs.push(paragraph);
                  }

                  return keptParagraphs;
                },
                [] as CustomMenuItem[]
              )

              if (keepers.length > 0) {
                newColumn.push(keepers);
              }

              return newColumn;
            },
            [] as CustomMenuItem[][]
          )
        }

        if (
          this.isFunctionalItem(topLevelItem) ||
          this.isCosmeticItem(topLevelItem) ||
          topLevelItem.items?.flat(1).some((item) => this.isFunctionalItem(item))
        ) {
          keptTopLevelItems.push(topLevelItem);
        }

        return keptTopLevelItems
      },
      [] as CustomMegaMenuItem[]
    );
  }

  private generateNavigationItems(brandingLinks: TranslatedInfoLinks): CustomMegaMenuItem[] {
    const items: CustomMegaMenuItem[] = [
      {
        label: $localize`:navigation|Domains menu:Domains`,
        items: [
          [
            {
              label: $localize`:navigation|Domain management section header:Management`,
              items: [
                {
                  label: $localize`:navigation|Domain portfolio link:Portfolio`,
                  routerLink: '/domains',
                  visible: this.featureAccessService.canReadSubject(PermissionSubjectClass.Domain)
                },
                {
                  label: $localize`:navigation|Register domains link:Register`,
                  routerLink: '/domains/check/new', visible: this.featureAccessService.canPerformAction(PermissionSubjectClass.Domain, PermittedAction.Register)
                },
                {
                  label: $localize`:navigation|Transfer domains link:Transfers`,
                  routerLink: '/domains/transfers/new',
                  visible: this.featureAccessService.canPerformAction(PermissionSubjectClass.Domain, PermittedAction.Transfer)
                },
                {
                  label: $localize`:navigation|External domains link:Externals`,
                  routerLink: '/domains/externals/new',
                  visible: this.featureAccessService.canPerformAction(PermissionSubjectClass.Domain, PermittedAction.Register)
                },
              ]
            }
          ],
          [
            {
              label: $localize`:navigation|Domain services section header:Services`,
              items: [
                {
                  label: $localize`:navigation|Check domain availability link:Availability`,
                  routerLink: '/domains/check/new',
                  visible: this.featureAccessService.canReadSubject(PermissionSubjectClass.Domain)
                },
                {
                  label: $localize`:navigation|Domain contacts link:Contacts`,
                  routerLink: '/contacts',
                  visible: this.featureAccessService.canReadSubject(PermissionSubjectClass.Contact)
                },
                {
                  label: $localize`:navigation|Contact sets link:Contact Sets`,
                  routerLink: '/contact_sets',
                  visible: this.featureAccessService.canReadSubject(PermissionSubjectClass.Contact)
                },
                {
                  label: $localize`:navigation|Nameserver sets link:Nameserver Sets`,
                  routerLink: '/nameserver_sets',
                  visible: this.featureAccessService.canReadSubject(PermissionSubjectClass.Nameserver)
                },
                {
                  label: $localize`:navigation|Bulk DNS management link:Bulk DNS`,
                  routerLink: '/dns/resource_records',
                  visible: this.featureAccessService.canReadSubject(PermissionSubjectClass.Domain)
                },
                {
                  label: $localize`:navigation|Domain groups link:Groups`,
                  routerLink: '/domain_groups',
                  visible: this.featureAccessService.canReadSubject(PermissionSubjectClass.DomainGroup)
                },
                {
                  label: $localize`:navigation|DNS Templates link:DNS Templates`,
                  routerLink: '/dns_templates',
                  visible: this.featureAccessService.canReadSubject(PermissionSubjectClass.DnsZone)
                },
                {
                  label: $localize`:navigation|TLD groups link:TLDs`,
                  routerLink: '/tld_groups',
                  visible: this.featureAccessService.canReadSubject(PermissionSubjectClass.TldGroup)
                },
              ]
            }
          ],
          [
            {
              label: $localize`:navigation|:Tools`,
              items: [
                {label: $localize`:navigation|Whois lookup tool:Whois`, routerLink: '/services/whois/new'},
                {label: $localize`:navigation|DNS dig tool:Dig`, routerLink: '/services/dig/new'},
                {label: $localize`:navigation|Nameserver check tool:Nameserver Check`, routerLink: '/services/nameserver_check/new'},
                {label: $localize`:navigation|IANA root zone database link:IANA Root Zone Database`, url: 'https://www.iana.org/domains/root/db', target: '_blank'},
                {label: $localize`:navigation|IDN punycode conversion tool:IDN/Punycode Conversion`, routerLink: '/services/idn_punycode_translation'},
                {
                  label: $localize`:navigation|Nameserver glue records link:Nameserver Glue Records`,
                  routerLink: '/nameservers',
                  visible: this.featureAccessService.canReadSubject(PermissionSubjectClass.Nameserver)
                },
                {
                  label: $localize`:navigation|DNS zone import tool:DNS Zone Import`,
                  routerLink: '/services/zone_import/new',
                  visible: this.featureAccessService.canPerformAction(PermissionSubjectClass.DnsZone, PermittedAction.Import)
                },
                {label: $localize`:navigation|DS records tool:DS Records`, routerLink: '/services/ds_records/new'},
              ]
            }
          ]
        ]
      },
      {
        label: $localize`:navigation|SSL certificates link:Certificates`,
        visible: this.featureAccessService.canReadSubject(PermissionSubjectClass.SSLCertificate),
        routerLink: '/ssl_certificates'
      },
      {
        label: $localize`:navigation|Brand protection menu:Brand Protection`,
        items: [
          [
            {
              label: $localize`:navigation|Brand protection portfolio section:Portfolio`,
              visible: this.featureAccessService.canReadSubject(PermissionSubjectClass.Monitoring),
              items: [
                {
                  label: $localize`:navigation|Brand Monitorin Portal external link:My Monitorings`,
                  url: this.brandMonitoringPortalURL,
                  visible: this.brandMonitoringPortalURL !== undefined
                },
                {
                  label: $localize`:navigation|Brand Monitoring Portal local services management:Monitoring Services`,
                  routerLink: '/monitoring_services'
                }
              ]
            },
            {
              label: $localize`:navigation|Brand protection domain name monitoring section:Monitorings (Domain Names)`,
              items: [
                {
                  label: $localize`:navigation|:Domain Audit`,
                  routerLink: '/brand_monitorings/WorkRequest::BrandMonitoring::DomainAudit/new',
                  visible: this.featureAccessService.canOrderBrandMonitoring(BrandProtectionFeature.BrandMonitoringDomainAudit)
                },
                {
                  label: $localize`:navigation|:Domain Monitoring`,
                  routerLink: '/brand_monitorings/WorkRequest::BrandMonitoring::DomainMonitoring/new',
                  visible: this.featureAccessService.canOrderBrandMonitoring(BrandProtectionFeature.BrandMonitoringDomainMonitoring)
                },
                {
                  label: $localize`:navigation|:Webpage and Whois Monitoring`,
                  routerLink: '/brand_monitorings/WorkRequest::BrandMonitoring::WebPageAndWhoisMonitoring/new',
                  visible: this.featureAccessService.canOrderBrandMonitoring(BrandProtectionFeature.BrandMonitoringWebPageAndWhoisMonitoring)
                },
                {
                  label: $localize`:navigation|:Registrant Search/Monitoring`,
                  routerLink: '/brand_monitorings/WorkRequest::BrandMonitoring::RegistrantSearch/new',
                  visible: this.featureAccessService.canOrderBrandMonitoring(BrandProtectionFeature.BrandMonitoringRegistrantSearch)
                },
              ]
            },
            {
              label: $localize`:navigation|Brand protection Internet monitorings section:Monitorings (Internet)`,
              items: [
                {
                  label: $localize`:navigation|Internet content watch link:Content Monitoring`,
                  routerLink: '/brand_monitorings/WorkRequest::BrandMonitoring::ContentMonitoring/new',
                  visible: this.featureAccessService.canOrderBrandMonitoring(BrandProtectionFeature.BrandMonitoringContentMonitoring)
                },
                {
                  label: $localize`:navigation|Social media watch link:Social Media`,
                  routerLink: '/brand_monitorings/WorkRequest::BrandMonitoring::SocialMediaMonitoring/new',
                  visible: this.featureAccessService.canOrderBrandMonitoring(BrandProtectionFeature.BrandMonitoringSocialMediaMonitoring)
                },
                {
                  label: $localize`:navigation|Logo watch link:Logo Monitoring`,
                  routerLink: '/brand_monitorings/WorkRequest::BrandMonitoring::LogoMonitoring/new',
                  visible: this.featureAccessService.canOrderBrandMonitoring(BrandProtectionFeature.BrandMonitoringLogoMonitoring)
                },
              ]
            }
          ],
          [
            {
              label: $localize`:navigation|:Services (Enforcement)`,
              items: [
                {
                  label: $localize`:navigation|Domain takedown service:Takedown`,
                  routerLink: '/brand_monitorings/WorkRequest::Takedown/new',
                  visible: this.featureAccessService.canOrderBrandMonitoring(BrandProtectionFeature.Takedown)
                },
                {
                  label: $localize`:navigation|Domain recovery service:Domain recovery`,
                  routerLink: '/brand_monitorings/WorkRequest::RecoverDomain/new',
                  visible: this.featureAccessService.canOrderBrandMonitoring(BrandProtectionFeature.RecoverDomain)
                },
                {
                  label: $localize`:navigation|WHOIS disclosure service:WHOIS disclosure/verification`,
                  routerLink: '/brand_monitorings/WorkRequest::DisclosureWhois/new',
                  visible: this.featureAccessService.canOrderBrandMonitoring(BrandProtectionFeature.DisclosureWhois)
                },
                {
                  label: $localize`:navigation|UDRP service link:Dispute resolution`,
                  routerLink: '/brand_monitorings/WorkRequest::ResolveDispute/new',
                  visible: this.featureAccessService.canOrderBrandMonitoring(BrandProtectionFeature.ResolveDispute)
                },
                {
                  label: $localize`:navigation|Consultation service:Consultation`,
                  routerLink: '/brand_monitorings/WorkRequest::BrandMonitoring::Consultation/new',
                  visible: this.featureAccessService.canOrderBrandMonitoring(BrandProtectionFeature.Consultation)
                },
              ]
            },
            {
              label: $localize`:navigation|:Services (Second Market)`,
              items: [
                {
                  label: $localize`:navigation|Anonymous domain purchase service:Domain acquisition`,
                  routerLink: '/brand_monitorings/WorkRequest::PurchaseDomain/new',
                  visible: this.featureAccessService.canOrderBrandMonitoring(BrandProtectionFeature.PurchaseDomain)
                },
                {
                  label: $localize`:navigation|Domain brokerage service:Domain brokerage`,
                  routerLink: '/brand_monitorings/WorkRequest::DomainBrokerage/new',
                  visible: this.featureAccessService.canOrderBrandMonitoring(BrandProtectionFeature.DomainBrokerage)
                },
                {
                  label: $localize`:navigation|Snapback service:Backorder`,
                  routerLink: '/brand_monitorings/WorkRequest::Snapback/new',
                  visible: this.featureAccessService.canOrderBrandMonitoring(BrandProtectionFeature.Snapback)
                },
                {
                  label: $localize`:navigation|Consultation service:Consultation`,
                  routerLink: '/brand_monitorings/WorkRequest::BrandMonitoring::Consultation/new',
                  visible: this.featureAccessService.canOrderBrandMonitoring(BrandProtectionFeature.Consultation)
                },
              ]
            }
          ],
          [
            {
              label: $localize`:navigation|Brands menu:Brands`,
              items: [
                {
                  label: $localize`:navigation|Brands management link:Brands`,
                  visible: this.featureAccessService.canReadSubject(PermissionSubjectClass.Brand),
                  routerLink: '/brands'
                },
                {
                  label: $localize`:navigation|Trademarks management link:Trademarks`,
                  visible: this.featureAccessService.canReadSubject(PermissionSubjectClass.Trademark),
                  routerLink: '/trademarks'
                },
                {
                  label: $localize`:navigation|Social media account management:Social Media`,
                  visible: this.featureAccessService.canReadSubject(PermissionSubjectClass.SocialMediaAccount),
                  routerLink: '/social_media_accounts'
                }
              ]
            }
          ]
        ]
      },
      {
        label: $localize`:navigation|Vendor information:Information`,
        items: [
          [
            {items: brandingLinks.links.map(infoLink => this.infoLinkToMenuItem(infoLink))}
          ]
        ]
      },
      {
        separator: true
      },
      {
        label: $localize`:navigation|:Reseller`,
        visible: this.featureAccessService.canSeeResellerSection(),
        items: [
          [
            {
              items: [
                {label: $localize`:navigation|Reseller account link:Accounts`, routerLink: '/reseller/accounts'},
                {label: $localize`:navigation|Reseller domain link:Domains`, routerLink: '/reseller/domains'},
                {label: $localize`:navigation|Reseller users link:Users`, routerLink: '/reseller/users'},
                {label: $localize`:navigation|Reseller logbook entries link:Logbook Entries`, routerLink: '/reseller/logbook/entries'},
              ]
            }
          ]
        ]
      },
      {
        label: $localize`:navigation|:Admin`,
        visible: this.featureAccessService.canSeeAdminSection(),
        items: [
          [
            {
              items: [
                {label: $localize`:navigation|:Accounts`, routerLink: '/admin/accounts'},
                {label: $localize`:navigation|:Work Requests`, routerLink: '/admin/work_requests'},
                {label: $localize`:navigation|:Domains`, routerLink: '/admin/domains'},
                {label: $localize`:navigation|:DNSSEC Keys`, routerLink: '/admin/dns_keys'},
                {label: $localize`:navigation|:SSL Certificates`, routerLink: '/admin/ssl_certificates'},
                {label: $localize`:navigation|:Logs`, routerLink: '/admin/logbook/entries'},
                {label: $localize`:navigation|:Users`, routerLink: '/admin/users'},
                {label: $localize`:navigation|:Contacts`, routerLink: '/admin/contacts'},
                {label: $localize`:navigation|:Statistics`, routerLink: '/admin/aggregated_stats'},
                {label: $localize`:navigation|:CNIC Reseller Events`, routerLink: '/admin/events'},
              ]
            }
          ],
          [
            {
              items: [
                {label: $localize`:navigation|:News`, routerLink: '/admin/news'},
                {label: $localize`:navigation|:TAN-Sets`, routerLink: '/admin/tan_sets'},
                {label: $localize`:navigation|:Balances`, routerLink: '/admin/balances'},
                {label: $localize`:navigation|:Bulk Price Change`, routerLink: '/admin/billing/bulk_price_changes/new'},
                {label: $localize`:navigation|:Invoices`, routerLink: '/admin/invoices'},
                {label: $localize`:navigation|:Invoice Items`, routerLink: '/admin/invoice_items'},
                {label: $localize`:navigation|:Recurring Billing`, routerLink: '/admin/billing/recurring_billings'},
                {label: $localize`:navigation|:Debtors List`, routerLink: '/admin/billing/debtors'},
                {label: $localize`:navigation|:User Categories`, routerLink: '/admin/user_categories'},
                {label: $localize`:navigation|:Single Sign-On`, routerLink: '/admin/sso_configs'},
                {label: $localize`:navigation|:Sessions`, routerLink: '/admin/user_sessions'},
              ]
            }
          ]
        ]
      },
      {
        label: this.currentUserName(false),
        items: [
          [
            {
              label: $localize`:navigation|Billing menu:Billing`,
              items: [
                {
                  label: $localize`:navigation|Invoices link:Invoices`,
                  routerLink: '/invoices',
                  visible: this.featureAccessService.canSeeInvoices()
                },
                {
                  label: $localize`:navigation|Billing settings link:Settings`,
                  routerLink: '/billing_configs',
                  visible: this.featureAccessService.canReadSubject(PermissionSubjectClass.BillingConfig)
                },
                {
                  label: $localize`:navigation|Bank details link:Bank Details`,
                  routerLink: '/bank_accounts',
                  visible: this.featureAccessService.canSeeInvoices()
                },
                {
                  label: $localize`:navigation|Credit cards link:Credit Cards`,
                  routerLink: '/credit_cards',
                  visible: this.featureAccessService.canPerformAction(PermissionSubjectClass.BillingConfig, PermittedAction.Update)
                },
                {
                  label: $localize`:navigation|View prices link:View Prices`,
                  routerLink: '/prices',
                  visible: this.featureAccessService.canSeePrices()
                },
                {
                  label: $localize`:navigation|Recurring billing link:Recurring Billing`,
                  routerLink: '/recurring_billings',
                  visible: this.featureAccessService.hasFeatureAccess(AuthFeature.RecurringBilling)
                },
              ]
            }
          ],
          [
            {
              label: $localize`:navigation|:Account Information`,
              items: [
                {label: $localize`:navigation|User news link:News`, routerLink: '/news'},
                {
                  label: $localize`:navigation|Open work requests link:Open Work`,
                  visible: this.featureAccessService.canSeeWorkRequests(),
                  routerLink: '/work_requests'
                },
                {label: $localize`:navigation|User notes link:Notes`, routerLink: '/notes'},
                {label: $localize`:navigation|User logs link:Logs`, routerLink: '/logbook/entries'},
                {
                  label: $localize`:navigation|Account users link:Users`,
                  routerLink: '/subusers',
                  visible: this.featureAccessService.canReadSubject(PermissionSubjectClass.User)
                },
                {
                  label: $localize`:navigation|Account divisions link:Divisions`,
                  routerLink: '/divisions',
                  visible: this.featureAccessService.canReadSubject(PermissionSubjectClass.Department)
                },
                {
                  label: $localize`:navigation|Account approval procedures:Approval`,
                  routerLink: '/approval/procedures',
                  visible: this.featureAccessService.canReadSubject(PermissionSubjectClass.ApprovalProcedure)
                },
                {
                  label: $localize`:navigation|User documents link:Files`,
                  routerLink: '/documents',
                  visible: this.featureAccessService.canReadSubject(PermissionSubjectClass.Document)
                },
                {
                  label: $localize`:navigation|Reseller branding settings:Branding`,
                  routerLink: '/branding_settings',
                  visible: this.featureAccessService.hasFeatureAccess(AuthFeature.BrandingManagement)
                },
              ]
            }
          ],
          [
            {
              label: $localize`:navigation|:Settings`,
              items: [
                {label: $localize`:navigation|User personal data link:Personal Data`, routerLink: '/personal_data/edit'},
                {label: $localize`:navigation|Account settings link:Account Settings`, routerLink: '/settings'},
                {separator: true},
                {
                  label: $localize`:navigation|User access tokens link:Access Tokens`,
                  routerLink: '/access_tokens',
                  visible: this.featureAccessService.canPerformAction(PermissionSubjectClass.AccessToken, PermittedAction.Manage)
                },
                {label: $localize`:navigation|User login sessions:Sessions`, routerLink: '/custom_sessions'},
                {
                  label: $localize`:navigation|Act as user:Act as user`,
                  command: () => this.openImpersonationPopup(),
                  visible: this.featureAccessService.canImpersonateUsers() && !this.isImpersonated()
                },
                {label: $localize`:navigation|Logout button:Logout`, command: () => this.logout()},
                {isImpersonationItem: true, visible: this.isImpersonated()},
                {isUserDataItem: true, visible: !this.isImpersonated()}
              ]
            }
          ]
        ]
      }
    ]

    return this.deleteEmptyMegaMenus(items);
  }

  ngAfterViewInit() {
    // Add event listener to detect iframe focus and close dropdowns
    this.setupIframeClickHandler();
    // Add global click event listener
    this.setupGlobalClickHandler();
  }

  ngOnDestroy() {
    // Clean up event listeners to prevent memory leaks
    if (this.windowBlurHandler) {
      window.removeEventListener('blur', this.windowBlurHandler);
    }
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
    }
    if (this.globalClickHandler) {
      document.removeEventListener('click', this.globalClickHandler);
    }
  }

  /**
   * Sets up global click event listener to log clicks in navbar outside megamenu ul
   */
  private setupGlobalClickHandler(): void {
    this.globalClickHandler = (event: Event) => {
      const target = event.target as HTMLElement;
      const navbarElement = document.querySelector('app-header nav') || document.querySelector('app-header');
      const megaMenuUl = document.querySelector('p-megamenu ul');

      // Check if the click is within the navbar but outside the megamenu ul
      const isInNavbar = navbarElement && navbarElement.contains(target);
      const isOutsideUl = !megaMenuUl || !megaMenuUl.contains(target);

      if (isInNavbar && isOutsideUl) {
        this.closeMegaMenu();
      }
    };
    document.addEventListener('click', this.globalClickHandler);
  }

  openImpersonationPopup() {
    this.dialogService.open(
      ImpersonatableUserSearchComponent,
      {
        closable: true,
        header: 'Impersonate User',
        style: { minWidth: '600px', minHeight: '400px' }
      }
    );
  }

  /**
   * Sets up event listener to detect clicks on iframe and close all open dropdowns
   */
  private setupIframeClickHandler(): void {
    // Listen for focus events on the window which can indicate iframe interaction
    this.windowBlurHandler = () => {
      // Small delay to ensure the iframe has received focus
      setTimeout(() => {
        if (document.activeElement?.tagName === 'IFRAME') {
          this.closeMegaMenu();
        }
      }, 10);
    };
    window.addEventListener('blur', this.windowBlurHandler);
  }

  /**
   * Closes the PrimeNG MegaMenu by finding and hiding any open overlay panels
   */
  private closeMegaMenu(): void {
    if (this.megaMenu) {
      // Find any open MegaMenu panels and close them by simulating an escape key press
      // or by removing active states
      const megaMenuElement = document.querySelector('p-megamenu');
      if (megaMenuElement) {
        // Look for any active menu items and remove their active state
        const activeItems = megaMenuElement.querySelectorAll('.p-menuitem-active');
        activeItems.forEach(item => {
          item.classList.remove('p-menuitem-active');
        });

        // Also trigger a document click to ensure any open overlays are closed
        const event = new Event('click', { bubbles: true });
        document.dispatchEvent(event);
      }
    }
  }

  /**
   * Checks if the current user has admin authorization.
   *
   * @returns {boolean} - True if the current user has admin authorization, false otherwise.
   */
  protected checkPermissions(): boolean {
    return <boolean>this.currentUser?.authorization.isAdmin
  }

  /**
   * Retrieves the current user's full name along with their company.
   *
   * @access protected
   *
   * @return {string} The current user's full name and company.
   */
  protected currentUserName(shouldShowCompany: boolean = true): string {
    return this.currentUser?.firstname + " " + this.currentUser?.lastname + (shouldShowCompany ? " (" + this.currentUser?.company + ", " + this.currentUser?.clientNumber + ")" : "");
  }

  /**
   * The current user's client number without leading zeros
   *
   * @access protected
   *
   * @return {string} Client number
   */
  protected formattedClientNumber(): string {
    return (this.currentUser?.clientNumber ?? '').replace(/^0+/, '');
  }

  protected currentEmail(): string {
    return this.currentUser?.email || '';
  }

  protected originalUserName(): string {
    const originalUser = this.impersonationService.originalUser()
    return originalUser?.firstname + " " + originalUser?.lastname
  }

  protected isImpersonated(): boolean {
    return this.impersonationService.isImpersonated();
  }

  protected stopImpersonation() {
    this.impersonationService.stopImpersonation();
  }

  /**
   * Performs logout action by sending a DELETE request to the legacy app's sign out endpoint.
   * This method also logs out the current user and navigates to the login page.
   * @async
   * @protected
   * @method
   * @returns {Promise<void>} - A promise that resolves once the logout process is complete.
   */
  protected async logout(): Promise<void> {
    await fetch(environment.legacyAppUrl + "/users/sign_out", {
      "headers": {
        "accept": "application/json",
        "content-type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": await this.authTokenService.getAuthenticityToken()
      },
      "method": "DELETE",
      "credentials": 'include'
    });
    this.currentUserService.logout();
    await this.router.navigateByUrl("/login");
  }

  logoUrl(): string {
    // Add a query parameter to avoid caching issues
    return appConstants.LOGO_URL + "?for=header";
  }
}
